import _ from "lodash"
import { Platform } from "react-native"
import SyncableModel from "../../database/SyncableModel"
import { BaseModelStoreProps } from "../base-model-store/base-model-store"

export const withBaseModel = (self) => ({
  views: {
    get storing() {
      return !!Object.values(self.pushIdQueue).find(v => v === 'storing')
    },
    get updating() {
      return !!Object.values(self.pushIdQueue).find(v => v === 'updating')
    },
    get deleting() {
      return !!Object.values(self.pushIdQueue).find(v => v === 'deleting')
    },
    get modifying() {
      return this.storing || this.updating || this.deleting
    }
  },
  actions: {
    set(key: keyof typeof BaseModelStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    initModel() {
      this.set('loading', false);
      this.set('needRefresh', false);
      this.set('pushIdQueue', {});
      this.set('error', null);
    },
    async storeModelToServer(Model: typeof SyncableModel, data, afterPushCallback = undefined) {
      const dataCleansed = _.reduce(
        _.keys(data),
        (m, key) => { m.setAttribute(key, data[key]); return m },
        new Model()
      ).toArray()

      const model = new Model()
      _.forOwn(_.pick(dataCleansed, model.getFillable()), (value, key) => model.setAttribute(key, value));
      return await model.storeToServer().then(() => true).catch((e) => {
        console.warn(e)
        this.set('error', e)
        return false
      }).then(async (success) => {
        afterPushCallback && await afterPushCallback(success, model)
        return success
      }).finally(() => {
        this.set('needRefresh', true)
      })
    },
    async storeModel(Model: typeof SyncableModel, data, waitPush = false, afterPushCallback = undefined) {
      const dataCleansed = _.reduce(
        _.keys(data),
        (m, key) => { m.setAttribute(key, data[key]); return m },
        new Model()
      ).toArray()

      const model = new Model();
      _.forOwn(_.pick(dataCleansed, model.getFillable()), (value, key) => model.setAttribute(key, value));
      if (Platform.OS === 'web') {
        return await model.storeToServer().then(() => true).catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        }).then(async (success) => {
          afterPushCallback && await afterPushCallback(success, model)
          return success
        }).finally(() => {
          this.set('needRefresh', true)
        })
      } else {
        const success = await model.save().catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        })
        if (!success) return false
        const key = model.getKey()
        this.set('pushIdQueue', {...self.pushIdQueue, [key]: 'storing'})
        const pushPromise = model.push().then(() => true).catch((e) => {
          console.warn(e)
          // this.set('error', e)
          return false
        }).then(async (success) => {
          afterPushCallback && await afterPushCallback(success, model)
          return success
        }).finally(() => {
          this.set('pushIdQueue', _.omit({...self.pushIdQueue}, key))
          this.set('needRefresh', true)
        })
        if (waitPush)
          return pushPromise.then(() => success);
        return success;
      }
    },
    async storeModelOffline(Model: typeof SyncableModel, data, afterSaveCallback = undefined) {
      const dataCleansed = _.reduce(
        _.keys(data),
        (m, key) => { m.setAttribute(key, data[key]); return m },
        new Model()
      ).toArray()

      const model = new Model();
      _.forOwn(_.pick(dataCleansed, model.getFillable()), (value, key) => model.setAttribute(key, value));
      if (Platform.OS === 'web') {
        return await model.storeToServer().then(() => true).catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        }).then(async (success) => {
          afterSaveCallback && await afterSaveCallback(success, model)
          return success
        }).finally(() => {
          this.set('needRefresh', true)
        })
      } else {
        const success = await model.save().catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        })
        if (!success) return false
        afterSaveCallback && await afterSaveCallback(success, model)
        this.set('needRefresh', true)
        return success;
      }
    },
    async updateModelToServer(Model: typeof SyncableModel, model: SyncableModel, data, afterPushCallback = undefined) {
      const dataCleansed = _.reduce(
        _.keys(data),
        (m, key) => { m.setAttribute(key, data[key]); return m },
        new Model()
      ).toArray()

      _.forOwn(_.pick(dataCleansed, model.getFillable()), (value, key) => model.setAttribute(key, value));
      const key = model.getKey()
      this.set('pushIdQueue', {...self.pushIdQueue, [key]: 'updating'})
      return await model.updateToServer().then(() => true).catch((e) => {
        console.warn(e)
        this.set('error', e)
        return false
      }).then(async (success) => {
        afterPushCallback && await afterPushCallback(success, model)
        return success
      }).finally(() => {
        this.set('pushIdQueue', _.omit({...self.pushIdQueue}, key))
        this.set('needRefresh', true)
      })
    },
    async updateModel(Model: typeof SyncableModel, model: SyncableModel, data, waitPush = false, afterPushCallback = undefined) {
      const dataCleansed = _.reduce(
        _.keys(data),
        (m, key) => { m.setAttribute(key, data[key]); return m },
        new Model()
      ).toArray()

      _.forOwn(_.pick(dataCleansed, model.getFillable()), (value, key) => model.setAttribute(key, value));
      if (Platform.OS === 'web') {
        const key = model.getKey()
        this.set('pushIdQueue', {...self.pushIdQueue, [key]: 'updating'})
        return await model.updateToServer().then(() => true).catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        }).then(async (success) => {
          afterPushCallback && await afterPushCallback(success, model)
          return success
        }).finally(() => {
          this.set('pushIdQueue', _.omit({...self.pushIdQueue}, key))
          this.set('needRefresh', true)
        })
      } else {
        const key = model.getKey()
        const success = await model.save().catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        })
        if (!success) return false
        this.set('pushIdQueue', {...self.pushIdQueue, [key]: 'updating'})
        const pushPromise = model.push().then(() => true).catch((e) => {
          console.warn(e)
          // this.set('error', e)
          return false
        }).then(async (success) => {
          afterPushCallback && await afterPushCallback(success, model)
          return success
        }).finally(() => {
          this.set('pushIdQueue', _.omit({...self.pushIdQueue}, key))
          this.set('needRefresh', true)
        })

        if (waitPush)
          return pushPromise.then(() => success);
        return success;
      }
    },
    async updateModelOffline(Model: typeof SyncableModel, model: SyncableModel, data, afterSaveCallback = undefined) {
      const dataCleansed = _.reduce(
        _.keys(data),
        (m, key) => { m.setAttribute(key, data[key]); return m },
        new Model()
      ).toArray()

      _.forOwn(_.pick(dataCleansed, model.getFillable()), (value, key) => model.setAttribute(key, value));
      if (Platform.OS === 'web') {
        return await model.updateToServer().then(() => true).catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        }).then(async (success) => {
          afterSaveCallback && await afterSaveCallback(success, model)
          return success
        }).finally(() => {
          this.set('needRefresh', true)
        })
      } else {
        const success = await model.save().catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        })
        if (!success) return false
        afterSaveCallback && await afterSaveCallback(success, model)
        this.set('needRefresh', true)
        return success;
      }
    },
    async deleteModelFromServer(Model: typeof SyncableModel, model: SyncableModel, afterPushCallback = undefined) {
      const key = model.getKey()
      this.set('pushIdQueue', {...self.pushIdQueue, [key]: 'deleting'})
      return await model.deleteFromServer().then(() => true).catch((e) => {
        console.warn(e)
        this.set('error', e)
        return false
      }).then(async (success) => {
        afterPushCallback && await afterPushCallback(success, model)
        return success
      }).finally(() => {
        this.set('pushIdQueue', _.omit({...self.pushIdQueue}, key))
        this.set('needRefresh', true)
      })
    },
    async deleteModel(Model: typeof SyncableModel, model: SyncableModel, waitPush = false, afterPushCallback = undefined) {
      if (Platform.OS === 'web') {
        const key = model.getKey()
        this.set('pushIdQueue', {...self.pushIdQueue, [key]: 'deleting'})
        return await model.deleteFromServer().then(() => true).catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        }).then(async (success) => {
          afterPushCallback && await afterPushCallback(success, model)
          return success
        }).finally(() => {
          this.set('pushIdQueue', _.omit({...self.pushIdQueue}, key))
          this.set('needRefresh', true)
        })
      } else {
        const key = model.getKey()
        const success = await model.delete().catch((e) => {
          console.warn(e)
          return false
        })
        if (!success) return false
        this.set('pushIdQueue', {...self.pushIdQueue, [key]: 'deleting'})
        const pushPromise = model.push().then(() => true).catch((e) => {
          console.warn(e)
          // this.set('error', e)
          return false
        }).then(async (success) => {
          afterPushCallback && await afterPushCallback(success, model)
          return success
        }).finally(() => {
          this.set('pushIdQueue', _.omit({...self.pushIdQueue}, key))
          this.set('needRefresh', true)
        })

        if (waitPush)
          return pushPromise.then(() => success);
        return success;
      }
    },
    async deleteModelOffline(Model: typeof SyncableModel, model: SyncableModel, afterSaveCallback = undefined) {
      if (Platform.OS === 'web') {
        return await model.deleteFromServer().then(() => true).catch((e) => {
          console.warn(e)
          this.set('error', e)
          return false
        }).then(async (success) => {
          afterSaveCallback && await afterSaveCallback(success, model)
          return success
        }).finally(() => {
          this.set('needRefresh', true)
        })
      } else {
        const success = await model.delete().catch((e) => {
          console.warn(e)
          return false
        })
        if (!success) return false
        afterSaveCallback && await afterSaveCallback(success, model)
        this.set('needRefresh', true)
        return success;
      }
    }
  }
})
